import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Lock, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { PasswordInput } from "../components/PasswordInput";
import api from "../lib/api";

const ResetPassword = () => {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const { setUser } = useAuth();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    if (!token) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center px-6" data-testid="reset-missing-token">
                <div className="text-center max-w-md">
                    <AlertCircle className="w-12 h-12 text-[#E53935] mx-auto mb-4" strokeWidth={1.5} />
                    <h1 className="font-heading text-2xl text-[#1C252E] mb-2">Reset link missing</h1>
                    <p className="text-sm text-[#4A5568] mb-5">This page needs a valid reset token. Request a fresh one below.</p>
                    <Link to="/forgot-password" className="inline-block bg-[#E53935] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:opacity-90 transition">
                        Request a new link
                    </Link>
                </div>
            </div>
        );
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirm) {
            toast.error("Passwords don't match");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/auth/reset-password", { token, new_password: password });
            if (data?.requires_2fa) {
                // Account has a security key enrolled — finish the password reset
                // by tapping the key on the login screen.
                toast.success("Password updated. Now tap your security key to sign in.");
                navigate("/login", { replace: true });
                return;
            }
            setUser(data);
            setSuccess(true);
            setTimeout(() => navigate(["admin", "board"].includes(data.role) ? "/admin" : "/account"), 1500);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Could not reset password");
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-[calc(100vh-160px)] flex items-center justify-center px-6 py-16" data-testid="reset-success">
                <div className="text-center max-w-md">
                    <CheckCircle2 className="w-12 h-12 text-[#E53935] mx-auto mb-4" strokeWidth={1.5} />
                    <h1 className="font-heading text-3xl text-[#1C252E] mb-2">Password updated</h1>
                    <p className="text-sm text-[#4A5568]">Signing you in…</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-[calc(100vh-160px)] flex items-center justify-center px-6 py-16" data-testid="reset-page">
            <div className="w-full max-w-md">
                <div className="text-center mb-10">
                    <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3">Account recovery</p>
                    <h1 className="font-heading text-4xl text-[#1C252E] font-medium tracking-tight" data-testid="reset-heading">
                        Choose a new password
                    </h1>
                    <p className="text-sm text-[#4A5568] mt-3">8+ characters, with at least one letter and one digit.</p>
                </div>

                <form onSubmit={handleSubmit} className="bg-white border border-black/5 rounded-3xl p-8 space-y-4">
                    <PasswordInput
                        id="reset-pw"
                        placeholder="New password"
                        autoComplete="new-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={8}
                        testId="reset-password-input"
                    />
                    <PasswordInput
                        id="reset-confirm"
                        placeholder="Confirm password"
                        autoComplete="new-password"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        required
                        minLength={8}
                        testId="reset-confirm-input"
                    />
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="reset-submit-btn"
                        className="w-full bg-[#1C252E] hover:bg-black text-white rounded-full py-3.5 font-medium transition-all disabled:opacity-60"
                    >
                        {loading ? "Updating…" : "Update password"}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ResetPassword;
